import { useEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'
import { useModalStore } from '@/hooks/useModalStore'
import { useViewportStore, useSelectionStore } from '@/stores'

const MIDDLE_CLICK_MAX_MS = 220
const MIDDLE_CLICK_MAX_DISTANCE_PX = 5

const IGNORE_SELECTORS = [
  '.etb',           // table elements
  '[data-action]',  // action buttons
  '#legend',        // canvas legend
  '#rel-legend',    // relation legend
]

const IGNORE_CLASSES = ['rel-handle', 'wp-handle', 'wp-add-hint']

function shouldIgnoreTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  if (IGNORE_SELECTORS.some((sel) => target.closest(sel))) return true
  if (IGNORE_CLASSES.some((cls) => target.classList.contains(cls))) return true
  if (target.tagName === 'circle') return true
  if (target.tagName.toLowerCase() === 'path') return true
  return false
}

function shouldIgnoreMiddleTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  return Boolean(target.closest('[data-action], #legend, #rel-legend, button, input, textarea, select, a'))
}

function oppositeMode(mode: 'pan' | 'select'): 'pan' | 'select' {
  return mode === 'pan' ? 'select' : 'pan'
}

/**
 * Enables pointer-based panning of the canvas.
 *
 * Pan delta is divided by current scale so that 1 screen-pixel of movement
 * always corresponds to (1/sc) canvas units — matching the legacy behaviour:
 *   tx += (mouseX - panStartX) / sc
 *
 * Reads canvasMode and relMode from selectionStore at event time to avoid
 * stale closures without re-registering listeners on mode changes.
 *
 * Returns `isPanning` so callers can adjust cursor.
 */
export function usePan(containerRef: RefObject<HTMLElement>): { isPanning: boolean } {
  const [isPanning, setIsPanning] = useState(false)
  const lastPos = useRef({ x: 0, y: 0 })
  const middleGesture = useRef<{
    pointerId: number
    originalMode: 'pan' | 'select'
    startedAt: number
    startX: number
    startY: number
    moved: boolean
  } | null>(null)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const handlePointerDown = (e: PointerEvent) => {
      const { canvasMode, relMode } = useSelectionStore.getState()

      if (relMode) return
      if (e.button === 1) {
        if (shouldIgnoreMiddleTarget(e.target)) return
        e.preventDefault()
        middleGesture.current = {
          pointerId: e.pointerId,
          originalMode: canvasMode,
          startedAt: Date.now(),
          startX: e.clientX,
          startY: e.clientY,
          moved: false,
        }
        useSelectionStore.getState().setCanvasMode('pan')
        const modalState = useModalStore.getState()
        if (modalState.fkPanel.open) modalState.closeFkPanel()
        if (modalState.indexPanel.open) modalState.closeIndexPanel()
        if (modalState.relationPopup.open) modalState.closeRelationPopup()
        lastPos.current = { x: e.clientX, y: e.clientY }
        el.setPointerCapture(e.pointerId)
        setIsPanning(true)
        return
      }
      if (canvasMode !== 'pan') return
      if (e.button !== 0) return
      if (shouldIgnoreTarget(e.target)) return

      e.preventDefault()
      const modalState = useModalStore.getState()
      if (modalState.fkPanel.open) modalState.closeFkPanel()
      if (modalState.indexPanel.open) modalState.closeIndexPanel()
      if (modalState.relationPopup.open) modalState.closeRelationPopup()
      lastPos.current = { x: e.clientX, y: e.clientY }
      el.setPointerCapture(e.pointerId)
      setIsPanning(true)
    }

    const handlePointerMove = (e: PointerEvent) => {
      if (!el.hasPointerCapture(e.pointerId)) return
      if (useSelectionStore.getState().canvasMode !== 'pan') return

      const middle = middleGesture.current
      if (middle?.pointerId === e.pointerId) {
        const distance = Math.hypot(e.clientX - middle.startX, e.clientY - middle.startY)
        if (distance > MIDDLE_CLICK_MAX_DISTANCE_PX) middle.moved = true
      }

      const { sc, panBy } = useViewportStore.getState()
      const dx = (e.clientX - lastPos.current.x) / sc
      const dy = (e.clientY - lastPos.current.y) / sc
      lastPos.current = { x: e.clientX, y: e.clientY }
      panBy(dx, dy)
    }

    const finishPointer = (e: PointerEvent, canceled: boolean) => {
      const middle = middleGesture.current
      if (middle?.pointerId === e.pointerId) {
        const quickClick = !canceled
          && !middle.moved
          && Date.now() - middle.startedAt <= MIDDLE_CLICK_MAX_MS
        useSelectionStore.getState().setCanvasMode(
          quickClick ? oppositeMode(middle.originalMode) : middle.originalMode,
        )
        middleGesture.current = null
      }
      if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId)
      setIsPanning(false)
    }

    const handlePointerUp = (e: PointerEvent) => finishPointer(e, false)
    const handlePointerCancel = (e: PointerEvent) => finishPointer(e, true)

    el.addEventListener('pointerdown', handlePointerDown)
    el.addEventListener('pointermove', handlePointerMove)
    el.addEventListener('pointerup', handlePointerUp)
    el.addEventListener('pointercancel', handlePointerCancel)

    return () => {
      if (middleGesture.current) {
        useSelectionStore.getState().setCanvasMode(middleGesture.current.originalMode)
        middleGesture.current = null
      }
      el.removeEventListener('pointerdown', handlePointerDown)
      el.removeEventListener('pointermove', handlePointerMove)
      el.removeEventListener('pointerup', handlePointerUp)
      el.removeEventListener('pointercancel', handlePointerCancel)
    }
  }, [containerRef])

  return { isPanning }
}
